import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, TrendingUp, Users, Wallet, Pencil, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, Field } from "../components/ui";
import { HeroPanel, Metric, ProgressBar, Step, TierRow } from "../components/ui-extras";
import PayoutSetup from "../components/PayoutSetup";
import TierBadge from "../components/TierBadge";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { useTranslation } from "react-i18next";

const TIERS = ["bronze", "silver", "gold", "platinum"];

/**
 * The investor's earnings home. Deliberately the most designed page in the app:
 * it has to persuade someone to keep publishing, and a wall of grey cards doesn't.
 * Every number shown is live from the partner settings, so what an investor reads
 * here is exactly what the admin configured — no hardcoded marketing figures.
 */
export default function Partner() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editingHandle, setEditingHandle] = useState(false);
  const [handle, setHandle] = useState("");

  const load = () => api.partnerMe().then((r) => { setData(r.data); setHandle(r.data.handle); })
    .catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const copy = () => {
    navigator.clipboard?.writeText(data.link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.success(t("partner.copied"));
  };

  const saveHandle = async () => {
    try {
      await api.setReferralHandle(handle);
      setEditingHandle(false); toast.success(t("dialogs.saved")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-48" /><Skeleton className="h-40" /></div>;
  if (!data) return null;

  const p = data.projection;
  const progress = data.tier_progress;

  return (
    <div className="space-y-6">
      <HeroPanel
        eyebrow={t("partner.eyebrow")}
        title={t("partner.title")}
        subtitle={t("partner.subtitle")}
        testid="partner-hero"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          <Metric tone="inverse" testid="p-pending" label={t("partner.pending")} value={money(data.pending_eur, "EUR")} />
          <Metric tone="inverse" testid="p-monthly" label={t("partner.monthlyRun")} value={money(p.monthly, "EUR")} sub={t("partner.perMonth")} />
          <Metric tone="inverse" testid="p-referrals" label={t("partner.subscribers")} value={p.active_referrals} sub={t("partner.converted", { total: data.referrals.total })} />
          <Metric tone="inverse" testid="p-annual" label={t("partner.annualised")} value={money(p.annual, "EUR")} />
        </div>
      </HeroPanel>

      {/* Referral link — the single thing an investor came here for. */}
      <Panel className="card p-5 sm:p-6" testid="partner-link">
        <div className="flex items-center gap-2 mb-1">
          <Wallet size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("partner.linkTitle")}</h2>
        </div>
        <p className="text-sm text-slate-500">{t("partner.linkBody")}</p>

        {editingHandle ? (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <div className="flex items-center flex-1 rounded-xl border border-slate-200 overflow-hidden">
              <span className="pl-3 pr-1 text-sm text-slate-400 shrink-0">/r/</span>
              <input className="flex-1 py-2.5 pr-3 outline-none text-sm" data-testid="handle-input"
                     value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())} />
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => { setEditingHandle(false); setHandle(data.handle); }}>
                {t("common.cancel")}
              </button>
              <button className="btn-primary" onClick={saveHandle} data-testid="save-handle">{t("common.save")}</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-xl bg-slate-50 px-4 py-3 text-sm tabular truncate" data-testid="referral-link">
              {data.link}
            </code>
            <div className="flex gap-2">
              <button className="btn-outline px-3" onClick={() => setEditingHandle(true)} data-testid="edit-handle"
                      title={t("partner.customise")}>
                <Pencil size={15} />
              </button>
              <button className="btn-primary" onClick={copy} data-testid="copy-link">
                {copied ? <Check size={15} /> : <Copy size={15} />} {t("partner.copy")}
              </button>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Users size={12} /> {t("partner.clicks", { n: data.clicks })}</span>
          {data.referral_credit_days > 0 && (
            <span className="flex items-center gap-1">
              <Info size={12} /> {t("partner.creditNote", { days: data.referral_credit_days })}
            </span>
          )}
        </div>
      </Panel>

      {/* What each tier pays — makes progression concrete rather than implied. */}
      <Panel className="card p-5 sm:p-6" testid="partner-tiers">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("partner.tiersTitle")}</h2>
        </div>
        <p className="text-sm text-slate-500">
          {t("partner.tiersBody", { base: money(p.distributable_base, "EUR") })}
        </p>

        <div className="mt-4 grid grid-cols-[1fr_auto_auto] gap-3 px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span>{t("partner.tier")}</span>
          <span className="text-right">{t("partner.share")}</span>
          <span className="text-right">{t("partner.perSubscriber")}</span>
        </div>
        <div className="space-y-1">
          {TIERS.map((tier) => (
            <TierRow
              key={tier}
              tier={tier}
              label={t(`tiers.${tier}`)}
              sharePct={p.by_tier[tier].share_pct}
              perSub={money(p.by_tier[tier].per_subscriber_monthly, "EUR")}
              current={p.tier === tier}
            >
              <TierBadge tier={tier} size="xs" />
            </TierRow>
          ))}
        </div>

        {progress?.next && (
          <div className="mt-5 rounded-xl bg-slate-50 p-4" data-testid="tier-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">
                {t("partner.nextTier", { tier: t(`tiers.${progress.next}`) })}
              </span>
              <span className="text-slate-400 tabular">{progress.pct}%</span>
            </div>
            <ProgressBar pct={progress.pct} className="mt-2" />
            <p className="text-xs text-slate-500 mt-2">
              {t("partner.nextTierHint", {
                points: progress.points_needed,
                months: progress.min_months,
                tier: t(`tiers.${progress.next}`),
              })}
              {progress.needs_approval && ` ${t("partner.needsApproval")}`}
            </p>
          </div>
        )}
      </Panel>

      {/* How it works — the questions every partner programme gets asked. */}
      <Panel className="card p-5 sm:p-6" testid="partner-how">
        <h2 className="font-heading font-semibold tracking-tight text-lg mb-4">{t("partner.howTitle")}</h2>
        <Step n={1} title={t("partner.step1")}>{t("partner.step1Body")}</Step>
        <Step n={2} title={t("partner.step2")}>{t("partner.step2Body")}</Step>
        <Step n={3} title={t("partner.step3")}>{t("partner.step3Body")}</Step>
        <Step n={4} title={t("partner.step4")}>
          {t("partner.step4Body", { min: money(data.min_payout_eur, "EUR") })}
        </Step>
        <Link to="/help/investor" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700">
          {t("partner.readGuide")} <ArrowRight size={14} />
        </Link>
      </Panel>

      <PayoutSetup />

      {data.by_period.length > 0 && (
        <Panel className="card p-5 sm:p-6" testid="partner-history">
          <h2 className="font-heading font-semibold tracking-tight text-lg mb-3">{t("partner.history")}</h2>
          <div className="divide-y divide-slate-50">
            {data.by_period.map((row) => (
              <div key={row.period} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-slate-600 tabular">{row.period}</span>
                <span className="font-medium tabular">{money(row.amount_eur, "EUR")}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
